import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import Modal from './Modal'

const NOTIFICATION_TYPES = [
  {
    type: 'friend_request',
    label: 'Friend Requests',
    description: 'When someone sends you a friend request',
  },
  {
    type: 'friend_accept',
    label: 'Friend Accepted',
    description: 'When someone accepts your friend request',
  },
  {
    type: 'message',
    label: 'Direct Messages',
    description: 'When someone sends you a direct message',
  },
  {
    type: 'mention',
    label: 'Mentions',
    description: 'When someone mentions you in a message',
  },
  {
    type: 'space_invite',
    label: 'Space Invites',
    description: 'When someone invites you to a space',
  },
  {
    type: 'watch_party_invite',
    label: 'Watch Party Invites',
    description: 'When someone invites you to a watch party',
  },
  {
    type: 'system',
    label: 'System Notifications',
    description: 'Important updates and announcements',
  },
]

/**
 * NotificationSettings Component
 * Modal for managing notification preferences
 */
export default function NotificationSettings({ isOpen, onClose, userId }) {
  const [preferences, setPreferences] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Load preferences on mount
  useEffect(() => {
    if (isOpen && userId) {
      loadPreferences()
    }
  }, [isOpen, userId])

  const loadPreferences = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', userId)

      if (error) throw error

      // Convert array to object for easy lookup
      const prefs = {}
      data?.forEach((pref) => {
        prefs[pref.notification_type] = {
          enabled: pref.enabled,
          sound_enabled: pref.sound_enabled,
        }
      })

      // Set defaults for types without preferences
      NOTIFICATION_TYPES.forEach((type) => {
        if (!prefs[type.type]) {
          prefs[type.type] = {
            enabled: true,
            sound_enabled: true,
          }
        }
      })

      setPreferences(prefs)
    } catch (err) {
      console.error('Error loading notification preferences:', err)
    } finally {
      setLoading(false)
    }
  }

  const togglePreference = async (notificationType, field) => {
    const currentValue = preferences[notificationType]?.[field] ?? true
    const newValue = !currentValue

    // Optimistic update
    setPreferences((prev) => ({
      ...prev,
      [notificationType]: {
        ...prev[notificationType],
        [field]: newValue,
      },
    }))

    try {
      setSaving(true)
      const { error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_id: userId,
          notification_type: notificationType,
          enabled: field === 'enabled' ? newValue : preferences[notificationType]?.enabled ?? true,
          sound_enabled: field === 'sound_enabled' ? newValue : preferences[notificationType]?.sound_enabled ?? true,
        })

      if (error) throw error
    } catch (err) {
      console.error('Error updating notification preference:', err)
      // Revert on error
      setPreferences((prev) => ({
        ...prev,
        [notificationType]: {
          ...prev[notificationType],
          [field]: currentValue,
        },
      }))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Notification Settings" size="large">
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-gjyl-indigo-500 mb-3"></div>
          <p className="text-sm text-slate-400">Loading preferences...</p>
        </div>
      ) : (
        <div className="space-y-1">
          {NOTIFICATION_TYPES.map((notif) => {
            const pref = preferences[notif.type] || { enabled: true, sound_enabled: true }

            return (
              <div
                key={notif.type}
                className="glass rounded-lg p-4 hover:glass-strong transition-all duration-200"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-white mb-1">
                      {notif.label}
                    </h4>
                    <p className="text-xs text-slate-400">{notif.description}</p>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Sound Toggle */}
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor={`sound-${notif.type}`}
                        className="text-xs text-slate-400"
                      >
                        Sound
                      </label>
                      <button
                        id={`sound-${notif.type}`}
                        onClick={() => togglePreference(notif.type, 'sound_enabled')}
                        disabled={!pref.enabled || saving}
                        className={`
                          relative inline-flex h-6 w-11 items-center rounded-full
                          transition-colors duration-200
                          ${pref.enabled && pref.sound_enabled
                            ? 'bg-gjyl-indigo-500'
                            : 'bg-gjyl-slate-700'
                          }
                          ${!pref.enabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                          ${saving ? 'opacity-50' : ''}
                        `}
                      >
                        <span
                          className={`
                            inline-block h-4 w-4 rounded-full bg-white
                            transition-transform duration-200
                            ${pref.enabled && pref.sound_enabled ? 'translate-x-6' : 'translate-x-1'}
                          `}
                        />
                      </button>
                    </div>

                    {/* Enable Toggle */}
                    <button
                      onClick={() => togglePreference(notif.type, 'enabled')}
                      disabled={saving}
                      className={`
                        relative inline-flex h-6 w-11 items-center rounded-full
                        transition-colors duration-200
                        ${pref.enabled ? 'bg-green-500' : 'bg-gjyl-slate-700'}
                        ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                      `}
                    >
                      <span
                        className={`
                          inline-block h-4 w-4 rounded-full bg-white
                          transition-transform duration-200
                          ${pref.enabled ? 'translate-x-6' : 'translate-x-1'}
                        `}
                      />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Footer with info */}
      <div className="mt-6 p-4 glass rounded-lg">
        <div className="flex items-start gap-3">
          <svg
            className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <p className="text-xs text-slate-300 mb-1">
              <strong>Tip:</strong> Disable specific notification types if you find them too
              distracting.
            </p>
            <p className="text-xs text-slate-400">
              System notifications cannot be disabled as they contain important security and
              platform updates.
            </p>
          </div>
        </div>
      </div>
    </Modal>
  )
}
